import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import LanguageSelector from '@/components/LanguageSelector';
import { Layers, Sun, Moon, ArrowLeft, Gift, Check, X } from 'lucide-react';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const isRegister = searchParams.get('mode') === 'register';
  const refCode = searchParams.get('ref'); // Get referral code from URL
  const [mode, setMode] = useState(isRegister || refCode ? 'register' : 'login');
  const [loading, setLoading] = useState(false);
  const { login, register, api } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    referralCode: refCode || ''
  });
  
  // Referral code validation state
  const [referralValidation, setReferralValidation] = useState({
    checking: false,
    valid: null,
    message: ''
  });

  // Validate referral code when it changes
  useEffect(() => {
    const validateCode = async () => {
      if (!formData.referralCode || formData.referralCode.length < 4) {
        setReferralValidation({ checking: false, valid: null, message: '' });
        return;
      }
      
      setReferralValidation({ checking: true, valid: null, message: '' });
      
      try {
        const response = await api.get(`/referral/validate/${formData.referralCode}`);
        setReferralValidation({
          checking: false,
          valid: response.data.valid,
          message: response.data.message
        });
      } catch (error) {
        setReferralValidation({
          checking: false,
          valid: false,
          message: 'Could not validate code'
        });
      }
    };
    
    const timer = setTimeout(validateCode, 500); // Debounce
    return () => clearTimeout(timer);
  }, [formData.referralCode, api]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'register') {
        await register(
          formData.email, 
          formData.password, 
          formData.firstName, 
          formData.lastName,
          formData.referralCode || null
        );
        toast.success('Account created! Welcome to MockupPro');
      } else {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 py-12">
        <div className="w-full max-w-md mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-12">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">{t('nav.back')}</span>
            </Link>
            <div className="flex items-center gap-2">
              <LanguageSelector variant="compact" />
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="auth-theme-toggle">
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-foreground rounded-xl flex items-center justify-center">
              <Layers className="w-6 h-6 text-background" />
            </div>
            <span className="font-semibold text-2xl tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MockupPro
            </span>
          </div>

          <motion.div
            key={mode}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h1 
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="auth-heading"
            >
              {mode === 'register' ? t('auth.createAccount') : t('auth.welcomeBack')}
            </h1>
            <p className="text-muted-foreground mb-8">
              {mode === 'register' 
                ? t('auth.trialInfo')
                : t('auth.signInInfo')
              }
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {mode === 'register' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">{t('auth.firstName')}</Label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder={t('auth.firstNamePlaceholder')}
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                      data-testid="first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">{t('auth.lastName')}</Label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder={t('auth.lastNamePlaceholder')}
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                {mode === 'login' && (
                  <div className="text-right">
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-muted-foreground hover:text-foreground hover:underline"
                      data-testid="forgot-password-link"
                    >
                      {t('auth.forgotPasswordLink')}
                    </Link>
                  </div>
                )}
              </div>
              
              {/* Referral Code Field (Registration only) */}
              {mode === 'register' && (
                <div className="space-y-2">
                  <Label htmlFor="referralCode" className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-primary" />
                    Referral Code (Optional)
                  </Label>
                  <div className="relative">
                    <Input
                      id="referralCode"
                      type="text"
                      placeholder="Enter referral code (optional)"
                      value={formData.referralCode}
                      onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toUpperCase() })}
                      className={referralValidation.valid === true ? 'border-green-500 pr-10' : 
                                 referralValidation.valid === false ? 'border-red-500 pr-10' : 'pr-10'}
                      data-testid="referral-code-input"
                    />
                    {referralValidation.checking && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                    {!referralValidation.checking && referralValidation.valid === true && (
                      <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                    )}
                    {!referralValidation.checking && referralValidation.valid === false && (
                      <X className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                    )}
                  </div>
                  {referralValidation.message && (
                    <p className={`text-xs ${referralValidation.valid ? 'text-green-600' : 'text-red-500'}`}>
                      {referralValidation.message}
                    </p>
                  )}
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full rounded-lg" 
                disabled={loading}
                data-testid="auth-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="spinner" /> {t('auth.processing')}
                  </span>
                ) : (
                  mode === 'register' ? t('auth.startTrial') : t('auth.signIn')
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-8">
              {mode === 'register' ? (
                <>
                  {t('auth.haveAccount')}{' '}
                  <button 
                    onClick={() => setMode('login')}
                    className="text-foreground hover:underline font-medium"
                    data-testid="switch-to-login"
                  >
                    {t('auth.signInLink')}
                  </button>
                </>
              ) : (
                <>
                  {t('auth.noAccount')}{' '}
                  <button 
                    onClick={() => setMode('register')}
                    className="text-foreground hover:underline font-medium"
                    data-testid="switch-to-register"
                  >
                    {t('auth.startTrialLink')}
                  </button>
                </>
              )}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 bg-muted/30 items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="w-24 h-24 bg-accent rounded-3xl flex items-center justify-center mx-auto mb-8">
            <Layers className="w-12 h-12" />
          </div>
          <h2 
            className="text-2xl font-bold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {t('auth.rightTitle')}
          </h2>
          <p className="text-muted-foreground">
            {t('auth.rightSubtitle')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
